import React from 'react';

import {
  ChakraBox,
  ChakraFlex,
  Divider,
  EmptySectionCard,
  SectionCard,
} from '@equip.health/ui';

import SectionHeader, {
  SectionHeaderAction,
  SectionHeaderBadge,
} from '~/components/common/SectionHeader';

import { RAIL_SECTION_ROW_CAP } from './rightRail.constants';
import { RailSectionErrorFallback } from './RailSectionErrorFallback';

export interface RailSectionProps {
  action?: SectionHeaderAction;
  badge?: SectionHeaderBadge;
  cap?: number;
  emptyCaption: string;
  errorLabel: string;
  headerText: string;
  id: string;
  isError?: boolean;
  /** With no rows yet, the section renders nothing until loading settles. */
  isLoading?: boolean;
  rows: React.ReactNode[];
}

// The row's own key keeps its state, such as an open popover, across a reorder.
const getRowKey = (row: React.ReactNode, index: number): React.Key =>
  React.isValidElement(row) ? (row.key ?? index) : index;

export const RailSection = ({
  action,
  badge,
  cap = RAIL_SECTION_ROW_CAP,
  emptyCaption,
  errorLabel,
  headerText,
  id,
  isError = false,
  isLoading = false,
  rows,
}: RailSectionProps) => {
  if (isError) {
    return <RailSectionErrorFallback id={`${id}__error`} label={errorLabel} />;
  }

  const isEmpty = rows.length === 0;
  if (isEmpty && isLoading) return null;

  const visibleRows = rows.slice(0, cap);
  // equip-ui Text renders bodyMedium as a <p>, so the named section is the landmark.
  const headerId = `${id}__header`;

  return (
    <ChakraBox
      aria-labelledby={headerId}
      as="section"
      data-testid={id}
      id={id}
      width="100%"
    >
      <SectionHeader
        action={
          action
            ? {
                ...action,
                ariaLabel: `${action.rightLinkText} ${headerText.toLowerCase()}`,
              }
            : undefined
        }
        badge={badge}
        headerText={headerText}
        id={headerId}
        variant="bodyMedium"
      />
      <ChakraBox marginTop="12px">
        {isEmpty ? (
          <EmptySectionCard caption={emptyCaption} id={id} />
        ) : (
          <SectionCard id={id}>
            {/* SectionCard is a row-direction Flex; wrap rows in a column. */}
            <ChakraFlex direction="column" gap="12px" width="100%">
              {visibleRows.map((row, index) => (
                <React.Fragment key={getRowKey(row, index)}>
                  {index > 0 && <Divider id={`${id}__divider-${index}`} />}
                  {row}
                </React.Fragment>
              ))}
            </ChakraFlex>
          </SectionCard>
        )}
      </ChakraBox>
    </ChakraBox>
  );
};
